/** Image-led field-guide cards, rendered locally without uploading user content. */
export interface SpeciesShareCardPayload {
  name: string;
  scientific?: string;
  category?: string;
  description: string;
  imageUrl?: string;
  imageCaption?: string;
  catalogNumber?: number;
  volume?: string;
  page?: number;
  bookTitle?: string;
}

const COLORS = { paper: '#f7f6f0', ink: '#183f38', muted: '#536961', rule: '#cdd6cc', photo: '#e7edf0', accent: '#80633c' };
const SANS = '"Noto Sans CJK SC", "PingFang SC", "Microsoft YaHei", sans-serif';
const SERIF = '"Noto Serif CJK SC", "Songti SC", "SimSun", serif';

/** Prefer a complete opening sentence over a dense, mid-statistic excerpt. */
export function cardDescription(value: string): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  const sentences = normalized.match(/[^。！？]+[。！？]?/g) || [];
  let summary = '';
  for (const sentence of sentences) {
    if (summary && Array.from(summary + sentence).length > 84) break;
    summary += sentence;
    if (Array.from(summary).length >= 84) break;
  }
  return summary;
}

/** Bounded Unicode-safe wrapping for fixed-size exports. */
export function cardTextLines(value: string, maxWidth: number, maxLines: number, measure: (value: string) => number): string[] {
  if (maxWidth <= 0 || maxLines <= 0) return [];
  const chars = Array.from(value.replace(/\s+/g, ' ').trim());
  const lines: string[] = [];
  let line = '';
  for (const char of chars) {
    if (line && measure(line + char) > maxWidth) {
      if (lines.length === maxLines - 1) {
        const tail = Array.from(line.trimEnd());
        while (tail.length && measure(tail.join('') + '…') > maxWidth) tail.pop();
        lines.push(tail.join('') + '…');
        return lines;
      }
      lines.push(line.trimEnd());
      line = char.trimStart();
    } else line += char;
  }
  if (line) lines.push(line.trimEnd());
  return lines;
}

function text(ctx: CanvasRenderingContext2D, value: string, x: number, y: number, width: number, size: number, lines = 1, font = `400 ${size}px ${SANS}`, color = COLORS.ink, lineHeight = size * 1.5) {
  ctx.font = font;
  ctx.fillStyle = color;
  const wrapped = cardTextLines(value, width, lines, (s) => ctx.measureText(s).width);
  wrapped.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
  return wrapped.length * lineHeight;
}

function loadCardImage(url?: string): Promise<HTMLImageElement | null> {
  if (!url || !/^(https?:\/\/|\/(?!\/))/i.test(url)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    const finish = (result: HTMLImageElement | null) => {
      clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      resolve(result);
    };
    const timer = setTimeout(() => { finish(null); image.src = ''; }, 8000);
    image.onload = () => finish(image.naturalWidth && image.naturalHeight ? image : null);
    image.onerror = () => finish(null);
    image.src = url;
  });
}

function rule(ctx: CanvasRenderingContext2D, y: number) {
  ctx.fillStyle = COLORS.rule;
  ctx.fillRect(72, y, 936, 1);
}

export async function renderSpeciesShareCard(payload: SpeciesShareCardPayload): Promise<{ url: string; imageIncluded: boolean }> {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1440;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('当前浏览器不支持生成图片，请使用分享链接');
  const image = await loadCardImage(payload.imageUrl);
  ctx.fillStyle = COLORS.paper;
  ctx.fillRect(0, 0, 1080, 1440);
  ctx.fillStyle = COLORS.ink;
  ctx.fillRect(0, 0, 1080, 12);

  text(ctx, '科普趣答 / 鱼虾图鉴', 72, 78, 640, 27, 1, `600 27px ${SANS}`);
  ctx.textAlign = 'right';
  text(ctx, 'AQUATIC ATLAS', 1008, 78, 300, 20, 1, `500 20px ${SANS}`, COLORS.muted);
  ctx.textAlign = 'left';
  rule(ctx, 110);
  text(ctx, payload.category || '水产品种', 72, 166, 600, 26, 1, `500 26px ${SANS}`, COLORS.accent);
  if (payload.catalogNumber != null) {
    ctx.textAlign = 'right';
    text(ctx, `NO. ${String(payload.catalogNumber).padStart(3, '0')}`, 1008, 166, 280, 26, 1, `400 26px Georgia, serif`, COLORS.muted);
    ctx.textAlign = 'left';
  }
  const titleSize = Array.from(payload.name).length > 12 ? 48 : Array.from(payload.name).length > 8 ? 62 : 84;
  const titleHeight = text(ctx, payload.name, 68, 265, 940, titleSize, 2, `700 ${titleSize}px ${SERIF}`, COLORS.ink, titleSize * 1.14);
  const scientificY = Math.max(322, 265 + titleHeight - titleSize * 1.14 + 48);
  text(ctx, payload.scientific || '水产种质资源档案', 72, scientificY, 936, 30, 1,
    payload.scientific ? 'italic 30px Georgia, "Times New Roman", serif' : `400 26px ${SANS}`, COLORS.muted);

  const photoY = Math.max(368, scientificY + 38);
  const photoHeight = 890 - photoY;
  ctx.fillStyle = COLORS.photo;
  ctx.fillRect(64, photoY, 952, photoHeight);
  if (image) {
    // Preserve all anatomical detail: no cropped fins/tails or synthetic species images.
    const scale = Math.min(952 / image.naturalWidth, photoHeight / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    ctx.drawImage(image, 64 + (952 - width) / 2, photoY + (photoHeight - height) / 2, width, height);
  } else {
    ctx.textAlign = 'center';
    text(ctx, payload.name, 540, photoY + photoHeight / 2, 780, 60, 1, `600 60px ${SERIF}`);
    text(ctx, '原书配图暂不可用', 540, photoY + photoHeight / 2 + 56, 780, 26, 1, `400 26px ${SANS}`, COLORS.muted);
    ctx.textAlign = 'left';
  }
  const credit = payload.imageCaption?.match(/(?:照片|图片|摄影)(?:提供)?[：:]\s*([^；;）)]+)/)?.[1];
  text(ctx, image ? `原书配图${credit ? ` / 照片提供：${credit}` : ''}` : '以图鉴资料认识水产种质资源', 72, 929, 936, 21, 1, `400 21px ${SANS}`, COLORS.muted);
  text(ctx, '品 种 笔 记', 72, 998, 936, 23, 1, `600 23px ${SANS}`, COLORS.accent);
  text(ctx, cardDescription(payload.description) || '该品种的资料正在整理中。', 72, 1050, 936, 32, 3, `400 32px ${SANS}`, COLORS.ink, 50);
  rule(ctx, 1196);
  text(ctx, '资料出处', 72, 1241, 936, 21, 1, `500 21px ${SANS}`, COLORS.muted);
  text(ctx, payload.bookTitle ? `《${payload.bookTitle}》` : '科普趣答 · 鱼虾图鉴', 72, 1283, 936, 26, 2, `500 26px ${SANS}`, COLORS.ink, 36);
  const locator = [payload.volume, payload.page != null ? `PDF 物理页 ${payload.page}` : undefined].filter(Boolean).join(' / ');
  text(ctx, locator || '水产种质资源科普资料', 72, 1373, 500, 22, 1, `400 22px ${SANS}`, COLORS.muted);
  ctx.textAlign = 'right';
  text(ctx, '认识一种水产 · 读懂一份自然', 1008, 1373, 450, 22, 1, `400 22px ${SERIF}`, COLORS.muted);
  return { url: canvas.toDataURL('image/png'), imageIncluded: !!image };
}
